import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { workforceDataService } from '../../services/WorkforceDataService';
import { CAPACITY_CATEGORY_LABELS, CAPACITY_CATEGORY_COLORS, LOW_COLOR, MISSING_COLOR, getCategory, getMetrics, getScale, isCategorical, getLegendTitle, getMissingLegend, formatMetricValue, readCountyValue, readStateValue } from '../../services/metricRegistry';
import { isMobile, isTablet } from 'react-device-detect';
const MapComponent = forwardRef(({ dashboardState, onStateClick, onCountyClick, onMapDataChange }, ref) => {
    const [mapOptions, setMapOptions] = useState(null);
    const [isLoading, setIsLoading] = useState(false); // Start with false to allow immediate updates
    const chartRef = useRef(null);
    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        zoomToLocation: (stateName, countyName) => {
            var _a, _b, _c, _d;
            if (!((_a = chartRef.current) === null || _a === void 0 ? void 0 : _a.chart)) {
                return;
            }
            const chart = chartRef.current.chart;
            const series = (_b = chart.series) === null || _b === void 0 ? void 0 : _b[0];
            if (!series)
                return;
            // Find the point by name
            const point = (_c = series.points) === null || _c === void 0 ? void 0 : _c.find((pt) => {
                var _a;
                if (countyName) {
                    // For counties, match both county and state
                    // Check original options state (contains geographic state)
                    // pt.state might contain interaction states like 'hover' or 'select'
                    const originalState = (_a = pt.options) === null || _a === void 0 ? void 0 : _a.state;
                    let pointStateName = originalState;
                    // For full US county map where point might not have state property, derive from hc-key
                    if (!pointStateName && !dashboardState.isDrilledDown) {
                        const stateHCKey = getStateHCKeyFromPoint(pt);
                        pointStateName = getStateNameFromKey(stateHCKey);
                    }
                    const matches = pt.name === countyName && pointStateName === stateName;
                    return matches;
                }
                else {
                    // For states, just match state name
                    return pt.name === stateName || pt.state === stateName;
                }
            });
            if (point) {
                // Select the point
                point.select(true, false);
                // Just select the point, no zooming (like index-v3i)
                // The map already shows the correct view when drilled down
            }
            else if (!countyName && dashboardState.geoLevel === 'county') {
                // If no specific point found but we're looking for a state in county view
                // Find all counties for that state
                const stateCounties = (_d = series.points) === null || _d === void 0 ? void 0 : _d.filter((pt) => pt.state === stateName);
                if (stateCounties && stateCounties.length > 0) {
                    // Just select the first county, no zooming
                    const firstCounty = stateCounties[0];
                    if (firstCounty) {
                        firstCounty.select(true, false);
                    }
                }
            }
        }
    }));
    // Wait for Highcharts scripts to load, then create or update the map
    useEffect(() => {
        // Always create a new map when geoLevel changes (state vs county use different map types)
        createMap();
    }, [dashboardState.geoLevel]);
    // Update only the data when category changes within the same geoLevel
    useEffect(() => {
        // Always create/update the map when metric changes
        // Adding a small delay to ensure the component has rendered
        //if (dashboardState.geoLevel != 'state') {
        const timeoutId = setTimeout(() => {
            if (!isLoading) {
                createMap();
            }
        }, 100);
        return () => clearTimeout(timeoutId);
        //}
    }, [dashboardState.selectedGroupId, dashboardState.selectedCategoryId, dashboardState.selectedMetricId]);
    // Update only the data when category changes within the same geoLevel
    useEffect(() => {
        // Always create/update the map when metric changes
        // Adding a small delay to ensure the component has rendered
        if (dashboardState.geoLevel != 'state') {
            const timeoutId = setTimeout(() => {
                if (!isLoading) {
                    createMap();
                }
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [dashboardState.isDrilledDown]);
    // Handle selection changes without recreating the map
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!((_a = chartRef.current) === null || _a === void 0 ? void 0 : _a.chart) || isLoading)
            return;
        const chart = chartRef.current.chart;
        const series = (_b = chart.series) === null || _b === void 0 ? void 0 : _b[0];
        if (!series)
            return;
        // Find and select the appropriate point
        if (dashboardState.selectedState && dashboardState.selectedState !== 'United States') {
            const point = (_c = series.points) === null || _c === void 0 ? void 0 : _c.find((pt) => {
                var _a;
                if (dashboardState.geoLevel === 'county' && dashboardState.selectedCounty) {
                    // Check original options state first (contains geographic state)
                    // pt.state might contain interaction states like 'hover' or 'select'
                    const originalState = (_a = pt.options) === null || _a === void 0 ? void 0 : _a.state;
                    let pointStateName = originalState;
                    // For full US county map where point might not have state property, derive from hc-key
                    if (!pointStateName && !dashboardState.isDrilledDown) {
                        const stateHCKey = getStateHCKeyFromPoint(pt);
                        pointStateName = getStateNameFromKey(stateHCKey);
                    }
                    const matches = pt.name === dashboardState.selectedCounty &&
                        (pointStateName === dashboardState.selectedState);
                    return matches;
                }
                else {
                    return pt.name === dashboardState.selectedState || pt.state === dashboardState.selectedState;
                }
            });
            if (point) {
                point.select(true, false);
            }
        }
        else {
            // Deselect all points if no state is selected
            (_d = series.points) === null || _d === void 0 ? void 0 : _d.forEach((pt) => {
                if (pt.selected) {
                    pt.select(false, false);
                }
            });
        }
    }, [dashboardState.selectedState, dashboardState.selectedCounty, dashboardState.stateCode]);
    // Cache for loaded state maps to avoid re-downloading
    const [loadedStateMaps, setLoadedStateMaps] = useState(new Set());
    // Function to dynamically load state-specific maps
    const loadStateMap = async (stateCode) => {
        var _a, _b;
        const stateMapKey = `countries/us/us-${stateCode.toLowerCase()}-all`;
        // Check if already loaded
        if ((_b = (_a = window.Highcharts) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b[stateMapKey]) {
            return true;
        }
        // Check if we've already tried to load this map and it failed
        if (loadedStateMaps.has(`failed-${stateCode}`)) {
            return false;
        }
        try {
            // Fetch the topology from local files
            //const mapUrl = `https://code.highcharts.com/mapdata/countries/us/us-${stateCode.toLowerCase()}-all.topo.json`;
            //console.log('Fetching state map from:', mapUrl);
            //const response = await fetch(mapUrl);
            //console.log('Fetch response:', { ok: response.ok, status: response.status });
            let response;
            if (stateCode.toUpperCase() === "AK") {
                response = await fetch(`/js/data/statemaps/us-${stateCode.toLowerCase()}-all.js`);
            }
            else {
                response = await fetch(`/js/data/statemaps/us-${stateCode.toLowerCase()}-all.topo.json`);
            }
            if (!response.ok) {
                console.warn(`State map not available for ${stateCode}`, response.status);
                setLoadedStateMaps(prev => new Set(prev).add(`failed-${stateCode}`));
                return false;
            }
            let topology;
            if (stateCode.toUpperCase() === "AK") {
                // For Alaska, the file is a JavaScript file, not JSON
                const jsContent = await response.text();
                // Execute the JavaScript content to register the map with Highcharts
                // The Alaska JS file should define the map data in Highcharts.maps
                try {
                    eval(jsContent);
                    // The Alaska JS file should have registered the map, so we can get it from Highcharts.maps
                    topology = window.Highcharts.maps[stateMapKey];
                    if (!topology) {
                        console.warn(`Alaska map not properly registered in Highcharts.maps after loading JS file`);
                        setLoadedStateMaps(prev => new Set(prev).add(`failed-${stateCode}`));
                        return false;
                    }
                }
                catch (error) {
                    console.warn(`Failed to execute Alaska JS file:`, error);
                    setLoadedStateMaps(prev => new Set(prev).add(`failed-${stateCode}`));
                    return false;
                }
            }
            else {
                topology = await response.json();
            }
            // Register the map with Highcharts
            if (window.Highcharts && topology) {
                // Remove projection metadata that causes rotation with local files
                //if (topology['hc-recommended-transform']) {
                //    delete topology['hc-recommended-transform'];
                //}
                //if (topology['hc-recommended-mapview']) {
                //    delete topology['hc-recommended-mapview'];
                //}
                window.Highcharts.maps[stateMapKey] = topology;
                setLoadedStateMaps(prev => new Set(prev).add(stateCode));
                return true;
            }
        }
        catch (error) {
            console.warn(`Failed to load state map for ${stateCode}:`, error);
            setLoadedStateMaps(prev => new Set(prev).add(`failed-${stateCode}`));
        }
        return false;
    };
    const generateMapData = async () => {
        // Use state-specific map when drilled down if available, otherwise use full US maps
        let mapKey;
        if (dashboardState.geoLevel === 'county') {
            if (dashboardState.isDrilledDown && dashboardState.stateCode) {
                // Try to load the state-specific map
                const stateMapLoaded = await loadStateMap(dashboardState.stateCode);
                if (stateMapLoaded) {
                    mapKey = `countries/us/us-${dashboardState.stateCode.toLowerCase()}-all`;
                }
                else {
                    // Fall back to full US county map with filtering
                    mapKey = 'countries/us/us-all-all';
                }
            }
            else {
                // Use full US county map
                mapKey = 'countries/us/us-all-all';
            }
        }
        else {
            // State level
            mapKey = 'countries/us/us-all';
        }
        // Generate data based on map type - using real data from service
        let data;
        const stateRegExPattern = /^countries\/us\/us-[a-z]{2}-all$/;
        const isCounty = mapKey === 'countries/us/us-all-all' || stateRegExPattern.test(mapKey);
        //const isCounty = mapKey === 'countries/us/us-all-all';
        if (isCounty) {
            //console.log('Generating county data with real values...');
            // For counties, get real data and map to geojson
            const workforceData = await workforceDataService.getCountyData();
            const counties = window.Highcharts.geojson(window.Highcharts.maps[mapKey]);
            data = counties.map((f, index) => {
                var _a;
                const hk = f.properties['hc-key'];
                let countyName = f.properties.name;
                if (f.properties['hc-key'] === "us-va-51600" || f.properties['hc-key'] === "us-va-600") {
                    countyName = "Fairfax City";
                }
                else if (f.properties['hc-key'] === "us-mo-510" || f.properties['hc-key'] === "us-mo-0") {
                    countyName = "St. Louis City";
                }
                else if (f.properties['hc-key'] === "us-va-51770" || f.properties['hc-key'] === "us-va-770") {
                    countyName = "Roanoke City";
                }
                else if (f.properties['hc-key'] === "us-va-51620" || f.properties['hc-key'] === "us-va-620") {
                    countyName = "Franklin City";
                }
                else if (f.properties['hc-key'] === "us-va-51760" || f.properties['hc-key'] === "us-va-760") {
                    countyName = "Richmond City";
                }
                else if (f.properties['hc-key'] === "us-md-510" || f.properties['hc-key'] === "us-md-0") {
                    countyName = "Baltimore City";
                }
                // For state-specific maps, we know all counties belong to that state
                let stateName;
                if (mapKey.startsWith('countries/us/us-') && mapKey.endsWith('-all') && mapKey !== 'countries/us/us-all-all') {
                    // Extract state from mapKey directly since all counties in this map belong to this state
                    const stateCodeFromMap = mapKey.replace('countries/us/us-', '').replace('-all', '');
                    const lookupKey = `us-${stateCodeFromMap}`;
                    stateName = getStateNameFromKey(lookupKey);
                }
                else {
                    // For full US map, extract state from the county's hc-key
                    stateName = getStateNameFromKey(getStateHCKeyFromPoint(f)) || 'Unknown';
                }
                // For state-specific maps, we already set the stateName above, so no need to filter
                // The state-specific map only contains counties for that state
                // Filter counties by selected state when drilled down (and using full US map)
                if (dashboardState.isDrilledDown && dashboardState.selectedState &&
                    dashboardState.selectedState !== 'United States' &&
                    mapKey === 'countries/us/us-all-all' &&
                    stateName !== dashboardState.selectedState) {
                    return null; // Skip counties not in the selected state
                }
                //// Debug logging for first few counties
                //if (index < 5) {
                //    console.log('Highcharts county:', { hk, countyName, stateName });
                //    if (index === 0) {
                //        console.log('Available states in data:', Object.keys(workforceData.data.counties));
                //    }
                //}
                /**
                 * Find county data with case-insensitive matching.
                 * Handles mismatch between frontend city names ("Fairfax City")
                 * and backend dictionary keys ("Fairfax city").
                 */
                const findCountyDataCaseInsensitive = (stateData, countyName) => {
                    // First try exact match (fast path)
                    if (stateData[countyName] != null) {
                        return stateData[countyName];
                    }
                    // Case-insensitive fallback
                    const lowerCountyName = countyName.toLowerCase();
                    for (const key in stateData) {
                        if (key.toLowerCase() === lowerCountyName) {
                            return stateData[key];
                        }
                    }
                    return null;
                };
                let value = null;
                // Use the stateName we already determined above
                const stateData = (_a = workforceData.data.counties) === null || _a === void 0 ? void 0 : _a[stateName];
                if (stateData != null) {
                    //if (getStateHCKeyFromPoint(f) === "us-la") {
                    //
                    //    console.log("here30", stateData[countyName]);
                    //
                    //}
                    const countyData = findCountyDataCaseInsensitive(stateData, countyName);
                    if (countyData != null) {
                        if (dashboardState.selectedCategoryId && dashboardState.selectedMetricId) {
                            // Get the metric value for the selected metric
                            value = readCountyValue(countyData, dashboardState.selectedMetricId);
                        }
                    }
                    //// Fallback to random if no data found
                    //if (value === null || value === undefined) {
                    //    const seed = (function s(str: string) {
                    //        let h = 0;
                    //        for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
                    //        const r = Math.sin(h) * 10000;
                    //        return r - Math.floor(r);
                    //    })(hk);
                    //    value = Math.round(5 + seed * 55); // 5..60
                    //
                    //    if (index < 5) {
                    //        console.log('Using fallback random value for:', countyName, 'value:', value);
                    //    }
                    //}
                    // Cache all metric values for this county for tooltip use (keyed by metric id)
                    const allMetrics = dashboardState.selectedCategoryId
                        ? getMetrics(dashboardState.selectedCategoryId, dashboardState.geoLevel)
                        : [];
                    const metricValues = {};
                    if (countyData && dashboardState.selectedCategoryId) {
                        allMetrics.forEach(metric => {
                            metricValues[metric.id] = readCountyValue(countyData, metric.id);
                        });
                    }
                    return {
                        'hc-key': hk,
                        value,
                        name: countyName,
                        state: stateName,
                        metricValues: metricValues
                    };
                }
                return {
                    'hc-key': hk,
                    value,
                    name: countyName,
                    state: stateName,
                    metricValues: null
                };
            }).filter((item) => item !== null); // Remove null values from filtered counties
        }
        else {
            // For states, get real data from the service
            const workforceData = await workforceDataService.getStateData();
            const stateGeoJson = window.Highcharts.geojson(window.Highcharts.maps[mapKey]);
            data = stateGeoJson.map((f) => {
                var _a;
                const hcKey = f.properties['hc-key']; // e.g., 'us-al', 'us-ak'
                const stateName = f.properties.name; // e.g., 'Alabama', 'Alaska'
                // Find matching state data
                let value = null;
                const stateData = (_a = workforceData.data.states) === null || _a === void 0 ? void 0 : _a[stateName];
                if (stateData) {
                    if (dashboardState.selectedCategoryId != null && dashboardState.selectedMetricId != null) {
                        // Get the metric value for the selected metric
                        value = readStateValue(stateData, dashboardState.selectedMetricId);
                    }
                    else {
                        // When no metric is selected, provide a default value to ensure clickability
                        //value = 1; // Small default value to make states clickable
                    }
                }
                // Cache all metric values for this state for tooltip use (keyed by metric id)
                const allMetrics = dashboardState.selectedCategoryId
                    ? getMetrics(dashboardState.selectedCategoryId, dashboardState.geoLevel)
                    : [];
                const metricValues = {};
                if (stateData && dashboardState.selectedCategoryId) {
                    allMetrics.forEach(metric => {
                        metricValues[metric.id] = readStateValue(stateData, metric.id);
                    });
                }
                return {
                    'hc-key': hcKey,
                    value,
                    name: stateName,
                    state: stateName,
                    metricValues: metricValues
                };
            });
        }
        return data;
    };
    const createMap = async () => {
        var _a, _b;
        setIsLoading(true);
        try {
            // Wait for Highcharts scripts to load
            await waitForHighchartsScripts();
            //console.log('Creating map for level:', dashboardState.geoLevel);
            // Use the same mapKey logic as generateMapData
            let mapKey;
            if (dashboardState.geoLevel === 'county') {
                if (dashboardState.isDrilledDown && dashboardState.stateCode) {
                    // Try to load the state-specific map
                    const stateMapLoaded = await loadStateMap(dashboardState.stateCode);
                    if (stateMapLoaded) {
                        mapKey = `countries/us/us-${dashboardState.stateCode.toLowerCase()}-all`;
                    }
                    else {
                        // Fall back to full US county map with filtering
                        mapKey = 'countries/us/us-all-all';
                    }
                }
                else {
                    // Use full US county map
                    mapKey = 'countries/us/us-all-all';
                }
            }
            else {
                // State level
                mapKey = 'countries/us/us-all';
            }
            //console.log('Using mapKey:', mapKey);
            //console.log('Map data exists?', !!window.Highcharts?.maps?.[mapKey]);
            //console.log('Available maps:', Object.keys(window.Highcharts?.maps || {}));
            // Generate data using the shared function
            const data = await generateMapData();
            // Simple loop to find min/max values (excluding zeros)
            let minValue = Infinity;
            let maxValue = -Infinity;
            let validCount = 0;
            if (data != null && data.length > 0) {
                for (let i = 0; i < data.length; i++) {
                    if (data[i] != null && data[i].value != null) {
                        const value = data[i].value;
                        if (value !== null && value !== undefined && !isNaN(value) && value > 0) {
                            validCount++;
                            if (value < minValue)
                                minValue = value;
                            if (value > maxValue)
                                maxValue = value;
                        }
                    }
                }
            }
            const colorAxisMin = validCount > 0 ? minValue : 0;
            const colorAxisMax = validCount > 0 ? maxValue : 20000;
            if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
                minValue = 0;
                maxValue = 1;
            }
            if (minValue === maxValue) {
                minValue = 0;
                maxValue = maxValue || 1;
            }
            // Calculate logarithmic transformation for color mapping (excluding zeros)
            const logMin = validCount > 0 ? Math.log10(minValue + 1) : Math.log10(2);
            const logMax = validCount > 0 ? Math.log10(maxValue + 1) : Math.log10(2);
            const logRange = logMax - logMin;
            //Special percentage based for annual earnings.
            // Collect all valid values and sort them for percentile-based color mapping
            const sortedValues = [];
            // Year 2 - the categorical shortage metric bypasses the continuous color
            // math entirely and maps its 0-3 quartile code onto discrete data classes.
            // Scale (classes/percentile/log) is derived per metric from the registry.
            const metricScale = getScale(dashboardState.selectedMetricId);
            const isCategoricalCapacity = isCategorical(dashboardState.selectedMetricId);
            const showLogaritmic = metricScale === 'log';
            if (isCategoricalCapacity) {
                data.forEach((pt) => {
                    if (pt != null) {
                        if (typeof pt.value === 'number' && Number.isFinite(pt.value)) {
                            // Raw quartile code (0-3) drives the discrete color classes
                            pt.hspercentagevalue = pt.value;
                        }
                        else {
                            pt.hspercentagevalue = null;
                            if (pt.value === "Missing") {
                                pt.color = MISSING_COLOR;
                            }
                        }
                    }
                });
            }
            else if (showLogaritmic === false) {
                data.forEach((pt) => {
                    if (pt != null && pt.value != null) {
                        const val = pt.value;
                        if (val != null && Number.isFinite(val) && val > 0) {
                            sortedValues.push(val);
                        }
                    }
                });
                sortedValues.sort((a, b) => a - b);
                // Linear percentage based
                // Assign percentile-based color values (0-100)
                data.forEach((pt) => {
                    if (pt != null && pt.value != null) {
                        const val = pt.value;
                        if (val != null && Number.isFinite(val) && val > 0) {
                            // Find the position of this value in the sorted array
                            const rank = sortedValues.findIndex(v => v >= val);
                            // Convert rank to percentile (0-100)
                            pt.hspercentagevalue = sortedValues.length > 1 ? (rank / (sortedValues.length - 1)) * 100 : 50;
                        }
                        else {
                            pt.hspercentagevalue = null;
                        }
                        if (pt.value === 0) {
                            pt.color = LOW_COLOR;
                        }
                        else if (pt.value === "<5") {
                            pt.color = LOW_COLOR;
                        }
                        else if (pt.value === "Missing") {
                            pt.color = MISSING_COLOR;
                        }
                    }
                });
            }
            else {
                //Logarithmic Classic
                data.forEach((pt) => {
                    if (pt != null && pt.value != null) {
                        const val = pt.value;
                        if (val != null && Number.isFinite(val) && val > 0) {
                            // Transform to logarithmic scale
                            const logVal = Math.log10(val + 1);
                            // Normalize to 0-100 range based on log scale
                            pt.hspercentagevalue = logRange > 0 ? ((logVal - logMin) / logRange) * 100 : 0;
                        }
                        else {
                            pt.hspercentagevalue = null;
                        }
                        if (pt.value === 0) {
                            pt.color = LOW_COLOR;
                        }
                        else if (pt.value === "<5") {
                            pt.color = LOW_COLOR;
                        }
                        else if (pt.value === "Missing") {
                            pt.color = MISSING_COLOR;
                        }
                    }
                });
            }
            // Helper function to format values for legend. The $/% affix and
            // K/M rounding come from the registry formatter (compact mode);
            // this only reconstructs the actual value from the color percentage.
            const formatLegendValue = (percentage) => {
                let actualValue;
                const logValue = (percentage / 100) * logRange + logMin;
                if (showLogaritmic === true) {
                    actualValue = Math.pow(10, logValue) - 1;
                }
                else {
                    // Convert percentile back to actual value using sorted values
                    const index = Math.round((percentage / 100) * (sortedValues.length - 1));
                    actualValue = sortedValues[Math.max(0, Math.min(index, sortedValues.length - 1))] || 0;
                }
                return formatMetricValue(dashboardState.selectedMetricId, actualValue, { compact: true });
            };
            // Detect if device is mobile or tablet for conditional responsive settings
            const shouldDisableTooltip = isMobile || isTablet;
            // Create the map configuration - exact same as original
            const config = {
                chart: {
                    map: mapKey,
                    backgroundColor: 'transparent',
                    height: null,
                    spacingTop: 0,
                    spacingBottom: 5,
                    spacingLeft: 10,
                    spacingRight: 10,
                    marginTop: 0,
                    //marginBottom: 0,
                    plotBorderWidth: 0,
                    events: {
                        load: function () {
                            // Adjust the "Missing or Suppressed" legend symbol position
                            const chart = this;
                            setTimeout(() => {
                                // Direct DOM manipulation approach
                                const container = chart.container;
                                const legendItems = container.querySelectorAll('.highcharts-legend-item');
                                // Target the second legend item (index 1)
                                if (legendItems.length > 1) {
                                    const secondLegendItem = legendItems[1];
                                    // The square marker is the path with class 'highcharts-point'
                                    const symbolMarker = secondLegendItem.querySelector('.highcharts-point');
                                    if (symbolMarker) {
                                        // Get current transform on the parent group
                                        const currentTransform = secondLegendItem.getAttribute('transform') || '';
                                        // Parse the translate values
                                        const match = currentTransform.match(/translate\(([^,]+),([^)]+)\)/);
                                        if (match) {
                                            const x = parseFloat(match[1]);
                                            const y = parseFloat(match[2]);
                                            // Update transform with adjusted X position
                                            secondLegendItem.setAttribute('transform', `translate(${x - 2},${y})`);
                                        }
                                    }
                                }
                            }, 0);
                        }
                    }
                },
                //mapZooming: true, // Enable zooming functionality
                title: { text: '' },
                mapNavigation: {
                    enabled: true,
                    buttonOptions: { verticalAlign: 'top', align: 'right' }
                },
                colorAxis: isCategoricalCapacity ? {
                    // Year 2 - discrete 4-class scale for the categorical shortage metric.
                    // Both arrays are indexed by the raw quartile code, so class i pairs
                    // the right severity label with the right shade (darkest = worst).
                    dataClasses: CAPACITY_CATEGORY_LABELS.map((label, i) => ({
                        from: i - 0.5,
                        to: i + 0.5,
                        name: label,
                        color: CAPACITY_CATEGORY_COLORS[i]
                    }))
                } : {
                    min: 0,
                    max: 100,
                    minColor: '#BBD7E9',
                    maxColor: '#0d47a1',
                    reversed: false,
                    labels: {
                        formatter: function () {
                            return formatLegendValue(Number(this.value));
                        }
                    },
                },
                legend: {
                    layout: 'vertical', align: 'right', verticalAlign: 'bottom', floating: true, width: 80,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 4, borderWidth: 1, borderColor: '#e5e7eb', padding: 10,
                    itemStyle: { fontSize: '.8em' },
                    symbolPadding: 5, // space between symbol and text
                    title: { text: getLegendTitle(dashboardState.selectedMetricId, dashboardState.geoLevel) || 'Scale', style: { fontSize: '11px', fontWeight: 'normal', whiteSpace: 'normal' } }
                },
                plotOptions: {
                    map: {
                        allAreas: true, // REQUIRED: Makes all map regions interactive, including those with null values
                        nullColor: '#f1f1f4', // Light gray for counties with no data
                        allowPointSelect: true, // Enable point selection for all areas
                        states: {
                            hover: {
                                color: '#f9e547',
                                borderColor: '#f9e547',
                                borderWidth: 2
                            },
                            select: {
                                color: undefined,
                                borderColor: '#f9e547',
                                borderWidth: 4
                            }
                        },
                        ...{ nullInteraction: true } // Enable interaction for null value points (bypass TS check)
                    }
                },
                series: [{
                        type: 'map',
                        data: data,
                        name: (_b = (_a = getCategory(dashboardState.selectedCategoryId)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : "",
                        joinBy: 'hc-key', // Link data to map regions using hc-key
                        keys: ['hc-key', 'value'], // Define the data structure keys
                        nullColor: '#f1f1f4', // Ensure null values are visible
                        allowPointSelect: true, // Enable point selection
                        enableMouseTracking: true, // Ensure mouse tracking is enabled for all points
                        dataLabels: {
                            enabled: false
                        },
                        colorKey: 'hspercentagevalue',
                        ...{ nullInteraction: true }, // Enable interaction for null value points (bypass TS check)
                        point: {
                            events: {
                                mouseOver: function () {
                                    const point = this;
                                    // Check if this is a Missing value (null or undefined)
                                    const isMissingValue = point.value === null || point.value === undefined ||
                                        point.hspercentagevalue === null || point.hspercentagevalue === undefined;
                                    if (isMissingValue) {
                                        // Hide the colorAxis crosshair marker for Missing values
                                        const chart = point.series.chart;
                                        const crosshair = chart.container.querySelector('path.highcharts-crosshair');
                                        if (crosshair) {
                                            //setTimeout(() => {
                                            crosshair.setAttribute('visibility', 'hidden');
                                            crosshair.style.setProperty('display', 'none', 'important');
                                            //}, 200);
                                        }
                                        // Add your custom code here for Missing counties
                                    }
                                },
                                mouseOut: function () {
                                    const point = this;
                                    // Restore the colorAxis crosshair marker visibility
                                    const chart = point.series.chart;
                                    const crosshair = chart.container.querySelector('path.highcharts-crosshair');
                                    if (crosshair) {
                                        crosshair.setAttribute('visibility', 'visible');
                                        crosshair.style.removeProperty('display');
                                    }
                                },
                                click: function () {
                                    var _a, _b;
                                    const point = this;
                                    if (dashboardState.geoLevel === 'county') {
                                        // For state-specific maps, use the state from dashboardState if we're drilled down
                                        let stateCode = getStateFromPoint(point);
                                        // If we're drilled down to a specific state, use that state code
                                        if (dashboardState.isDrilledDown && dashboardState.stateCode) {
                                            stateCode = dashboardState.stateCode;
                                        }
                                        // Get the state name - prioritize options.state over state property
                                        // because point.state might contain interaction state like 'hover' or 'select'
                                        let stateName = (_a = point.options) === null || _a === void 0 ? void 0 : _a.state;
                                        if (!stateName) {
                                            // For full US county map, derive state name from the county's hc-key
                                            const stateHCKey = getStateHCKeyFromPoint(point);
                                            stateName = getStateNameFromKey(stateHCKey);
                                        }
                                        // For non-drilled-down view, select the point immediately
                                        // This ensures visual feedback before any map recreation
                                        if (!dashboardState.isDrilledDown) {
                                            point.select(true, false);
                                        }
                                        onCountyClick(point.name, stateCode, stateName);
                                    }
                                    else {
                                        const stateCode = ((_b = point['hc-key']) === null || _b === void 0 ? void 0 : _b.replace('us-', '')) || '';
                                        const stateName = getStateNameFromKey(point['hc-key']) || 'Unknown State';
                                        onStateClick(stateName, stateCode);
                                    }
                                    // Adjust the "Missing or Suppressed" legend symbol position
                                    const chart = this;
                                    setTimeout(() => {
                                        var _a;
                                        // Direct DOM manipulation approach
                                        const container = (_a = point === null || point === void 0 ? void 0 : point.chart) === null || _a === void 0 ? void 0 : _a.container;
                                        const legendItems = container === null || container === void 0 ? void 0 : container.querySelectorAll('.highcharts-legend-item');
                                        // Target the second legend item (index 1)
                                        if ((legendItems === null || legendItems === void 0 ? void 0 : legendItems.length) > 1) {
                                            const secondLegendItem = legendItems[1];
                                            // The square marker is the path with class 'highcharts-point'
                                            const symbolMarker = secondLegendItem.querySelector('.highcharts-point');
                                            if (symbolMarker) {
                                                // Get current transform on the parent group
                                                const currentTransform = secondLegendItem.getAttribute('transform') || '';
                                                // Parse the translate values
                                                const match = currentTransform.match(/translate\(([^,]+),([^)]+)\)/);
                                                if (match) {
                                                    const x = parseFloat(match[1]);
                                                    const y = parseFloat(match[2]);
                                                    // Update transform with adjusted X position
                                                    secondLegendItem.setAttribute('transform', `translate(${x - 2},${y})`);
                                                }
                                            }
                                        }
                                    }, 0);
                                }
                            }
                        }
                        // Note: colorByPoint: true conflicts with colorAxis, so we remove it
                    },
                    {
                        type: 'line',
                        name: getMissingLegend(dashboardState.selectedCategoryId, dashboardState.geoLevel).label,
                        color: '#E6E1F0',
                        data: [],
                        showInLegend: getMissingLegend(dashboardState.selectedCategoryId, dashboardState.geoLevel).show,
                        enableMouseTracking: false,
                        lineWidth: 0,
                        marker: {
                            symbol: 'square',
                            radius: 12,
                            lineWidth: 0,
                            lineColor: '#E6E1F0',
                        },
                        states: {
                            hover: {
                                enabled: false
                            },
                            inactive: {
                                enabled: false,
                                opacity: 1
                            }
                        },
                        events: {
                            legendItemClick: function () {
                                return false;
                            }
                        }
                    },],
                xAxis: { visible: false, title: { text: null } },
                yAxis: { visible: false, title: { text: undefined } },
                tooltip: {
                    useHTML: true, padding: 0, borderWidth: 0, backgroundColor: 'transparent', shadow: false,
                    className: 'hc-tooltip',
                    formatter: function () {
                        var _a;
                        const point = this.point || this;
                        const p = point;
                        const labels = (typeof getCurrentKpiLabels === 'function') ? getCurrentKpiLabels() : [];
                        const st = getStateFromPoint(point);
                        const countyName = point.name || '';
                        const title = `${countyName}${st ? ` (${st})` : ''}`;
                        const sub = ((_a = getCategory(dashboardState.selectedCategoryId)) === null || _a === void 0 ? void 0 : _a.label) || '';
                        if (!labels.length) {
                            return `
              <div class="tt-card">
                <div class="tt-title">${title}</div>
                ${sub ? `<div class="tt-sub">${sub}</div>` : ''}
                <div class="tt-list">
                  <div class="l">Value</div>
                  <div class="v">${formatMetricValue(dashboardState.selectedMetricId, p.value)}</div>
                </div>
              </div>
            `;
                        }
                        const allMetrics = dashboardState.selectedCategoryId
                            ? getMetrics(dashboardState.selectedCategoryId, dashboardState.geoLevel)
                            : [];
                        // Year 2 formatting rules ($/% affixes, shortage level labels, Age
                        // decimals, suppression sentinels) all live in formatMetricValue.
                        const rowLabels = allMetrics.map(metric => { var _a, _b; return (_b = (_a = metric.labelByGeo) === null || _a === void 0 ? void 0 : _a[dashboardState.geoLevel]) !== null && _b !== void 0 ? _b : metric.label; });
                        const rows = allMetrics.map((metric, i) => {
                            const metricValue = p.metricValues ? p.metricValues[metric.id] : null;
                            return `
                            <div class="l">${rowLabels[i]}</div><div class="v">
                            ${formatMetricValue(metric.id, metricValue)}
                           </div>`;
                        }).join('');
                        // The category heading is dropped when the rows beneath it already
                        // say the same word. It earns its place for a category holding
                        // several metrics — it names what the rows have in common — but
                        // Female holds exactly one, whose label IS the category label, so
                        // the tooltip printed "Female" with no value and then "Female"
                        // with one. The row is the line that keeps its value, so the
                        // heading is what goes.
                        //
                        // Tested on the labels rather than on the category id: any future
                        // single-metric category inherits the fix, and one that renames
                        // its metric away from its own label keeps its heading without
                        // anything here needing to know it happened.
                        const subIsRedundant = rowLabels.length === 1 &&
                            rowLabels[0].trim().toLowerCase() === sub.trim().toLowerCase();
                        return `
            <div class="tt-card">
              <div class="tt-title">${title}</div>
              ${subIsRedundant ? '' : `<div class="tt-sub">${sub}</div>`}
              <div class="tt-list">${rows}</div>
            </div>
          `;
                    }
                },
                credits: { enabled: false },
                exporting: { enabled: false },
                responsive: {
                    rules: [{
                            condition: {
                                maxWidth: 768
                            },
                            chartOptions: {
                                chart: {
                                    height: null,
                                    spacingTop: 0,
                                    spacingBottom: 0,
                                    spacingLeft: 0,
                                    spacingRight: 0,
                                    marginTop: 0,
                                    marginBottom: 0,
                                    marginLeft: 0,
                                    marginRight: 0,
                                    reflow: true
                                },
                                legend: {
                                    enabled: false,
                                    layout: 'vertical',
                                    align: 'right',
                                    verticalAlign: 'bottom',
                                    floating: true,
                                    width: 65,
                                    backgroundColor: '#FFFF00',
                                    borderRadius: 4,
                                    borderWidth: 1,
                                    borderColor: '#e5e7eb',
                                    padding: 6,
                                    itemStyle: { fontSize: '10px' },
                                    title: {
                                        text: getLegendTitle(dashboardState.selectedMetricId, dashboardState.geoLevel) || 'Scale',
                                        style: { fontSize: '9px', fontWeight: 'normal', whiteSpace: 'normal' }
                                    }
                                },
                                mapNavigation: {
                                    enabled: false,
                                    buttonOptions: {
                                        align: 'right',
                                        verticalAlign: 'top',
                                        height: 18,
                                        width: 18
                                    }
                                },
                                tooltip: {
                                    enabled: false
                                }
                            },
                        },
                        {
                            condition: {
                                maxWidth: 1050,
                            },
                            chartOptions: {
                                // Only disable tooltips on mobile/tablet devices, not desktops
                                tooltip: {
                                    enabled: !shouldDisableTooltip
                                }
                            },
                        }]
                }
            };
            setMapOptions(config);
            // Send the data to parent after map is created
            if (onMapDataChange) {
                onMapDataChange(data);
            }
            // Restore state selection after map recreation
            setTimeout(() => {
                var _a, _b, _c;
                if (((_a = chartRef.current) === null || _a === void 0 ? void 0 : _a.chart) && dashboardState.selectedState && dashboardState.selectedState !== 'United States') {
                    const chart = chartRef.current.chart;
                    const series = (_b = chart.series) === null || _b === void 0 ? void 0 : _b[0];
                    if (series) {
                        const point = (_c = series.points) === null || _c === void 0 ? void 0 : _c.find((pt) => {
                            var _a;
                            if (dashboardState.geoLevel === 'county' && dashboardState.selectedCounty) {
                                const currentState = pt.state;
                                const originalState = (_a = pt.options) === null || _a === void 0 ? void 0 : _a.state;
                                const matches = pt.name === dashboardState.selectedCounty &&
                                    (currentState === dashboardState.selectedState || originalState === dashboardState.selectedState);
                                return matches;
                            }
                            else {
                                return pt.name === dashboardState.selectedState || pt.state === dashboardState.selectedState;
                            }
                        });
                        if (point) {
                            point.select(true, false);
                        }
                    }
                }
            }, 10); // Increased timeout to ensure map is fully rendered
            // Don't zoom automatically here - let the parent component control zoom
        }
        catch (error) {
            console.error('Error creating map:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    // Metric descriptors for the current category/geo (drives tooltip rows)
    const getCurrentKpiLabels = () => {
        if (!(dashboardState === null || dashboardState === void 0 ? void 0 : dashboardState.selectedCategoryId))
            return [];
        return getMetrics(dashboardState.selectedCategoryId, dashboardState.geoLevel);
    };
    // Return a 2-letter state code for either a state point or a county point.
    const getStateFromPoint = (p) => {
        const props = (p === null || p === void 0 ? void 0 : p.properties) || {};
        const hk = props['hc-key'] || p['hc-key'] || '';
        // County or combined maps: "us-xx-###" → "XX"
        const m = /^us\-([a-z]{2})\-/i.exec(hk);
        if (m)
            return m[1].toUpperCase();
        // State-only map fallback: many state shapes have hc-a2 = "TX", "MA", etc.
        const a2 = props['hc-a2'];
        if (a2 && a2.length === 2)
            return a2;
        return '';
    };
    // Return a 2-letter state code for either a state point or a county point.
    const getStateHCKeyFromPoint = (p) => {
        const props = (p === null || p === void 0 ? void 0 : p.properties) || {};
        const hk = props['hc-key'];
        // For state-specific maps, the hc-key might be in format "va-001" instead of "us-va-001"
        if (hk && hk.match(/^[a-z]{2}-\d{3}$/i)) {
            return `us-${hk.substring(0, 2)}`;
        }
        // Original logic for full US maps
        return hk ? hk.substring(0, 5) : '';
    };
    // Helper function to wait for Highcharts scripts to load
    const waitForHighchartsScripts = () => {
        return new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max wait
            const checkMapData = () => {
                attempts++;
                if (attempts > maxAttempts) {
                    reject(new Error('Map scripts failed to load'));
                    return;
                }
                if (window.Highcharts &&
                    window.Highcharts.maps &&
                    window.Highcharts.mapChart &&
                    window.Highcharts.maps['countries/us/us-all'] &&
                    window.Highcharts.maps['countries/us/us-all-all']) {
                    resolve();
                }
                else {
                    setTimeout(checkMapData, 100);
                }
            };
            setTimeout(checkMapData, 100);
        });
    };
    // Helper function to get state name from hc-key
    const getStateNameFromKey = (hcKey) => {
        const stateNames = {
            'us-al': 'Alabama', 'us-ak': 'Alaska', 'us-az': 'Arizona', 'us-ar': 'Arkansas',
            'us-ca': 'California', 'us-co': 'Colorado', 'us-ct': 'Connecticut', 'us-de': 'Delaware',
            'us-dc': 'District of Columbia', 'us-fl': 'Florida', 'us-ga': 'Georgia', 'us-hi': 'Hawaii',
            'us-id': 'Idaho', 'us-il': 'Illinois', 'us-in': 'Indiana', 'us-ia': 'Iowa',
            'us-ks': 'Kansas', 'us-ky': 'Kentucky', 'us-la': 'Louisiana', 'us-me': 'Maine',
            'us-md': 'Maryland', 'us-ma': 'Massachusetts', 'us-mi': 'Michigan', 'us-mn': 'Minnesota',
            'us-ms': 'Mississippi', 'us-mo': 'Missouri', 'us-mt': 'Montana', 'us-ne': 'Nebraska',
            'us-nv': 'Nevada', 'us-nh': 'New Hampshire', 'us-nj': 'New Jersey', 'us-nm': 'New Mexico',
            'us-ny': 'New York', 'us-nc': 'North Carolina', 'us-nd': 'North Dakota', 'us-oh': 'Ohio',
            'us-ok': 'Oklahoma', 'us-or': 'Oregon', 'us-pa': 'Pennsylvania', 'us-ri': 'Rhode Island',
            'us-sc': 'South Carolina', 'us-sd': 'South Dakota', 'us-tn': 'Tennessee', 'us-tx': 'Texas',
            'us-ut': 'Utah', 'us-vt': 'Vermont', 'us-va': 'Virginia', 'us-wa': 'Washington',
            'us-wv': 'West Virginia', 'us-wi': 'Wisconsin', 'us-wy': 'Wyoming'
        };
        return stateNames[hcKey] || hcKey;
    };
    if (isLoading) {
        return (_jsx("div", { className: "d-flex justify-content-center align-items-center map-container-loading", style: { height: '525px' }, children: _jsx("div", { className: "spinner-border text-primary", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading chart..." }) }) }));
    }
    if (!mapOptions) {
        return (_jsx("div", { className: "d-flex justify-content-center align-items-center map-container-error", style: { height: '525px' }, children: _jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-muted", children: "Failed to load chart data" }), _jsx("button", { className: "btn btn-primary btn-sm", onClick: createMap, children: "Retry" })] }) }));
    }
    return (_jsx("div", { id: "highcharts-map", style: { position: 'relative', width: '100%', height: '525px' }, children: _jsx(HighchartsReact, { highcharts: window.Highcharts || Highcharts, constructorType: 'mapChart', options: mapOptions, containerProps: { style: { width: '100%', height: '100%' } }, ref: chartRef }) }));
});
MapComponent.displayName = 'MapComponent';
export default MapComponent;
